// pages/Projects.jsx
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ListTable from '../components/ListTable';
import Container from '../layouts/Container';
import { getAll, deleteProject, restoreProject } from '../services/project/projectService';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Badge } from 'react-bootstrap';
import EditProjectModal from '../components/EditProjectModal';
import { Pencil, XOctagon, Check2Circle } from 'react-bootstrap-icons';
import AnchorTag from '../components/AnchorTag';
import { useNavigate } from 'react-router-dom';
import { useUsers } from '../context/UserContext';
import { boolean } from 'yup';

function Projects() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useUsers();
  const isAnalyst = user?.roles.some(role => role.name === 'GVE_Analyst');

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await getAll({ includeDeleted: true }); // ⬅️ include soft-deleted
      if (!res.success) {
        toast.error(res.data?.errorMessage || 'Error fetching projects.');
        return;
      }
      setProjects(res.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error(error?.response?.data?.errorMessage || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  const handleDelete = async (projectId) => {
    const confirmed = window.confirm('Delete this project?');
    if (!confirmed) return;
    try {
      const res = await deleteProject(projectId);
      if (!res.success) {
        toast.error(res.data?.errorMessage || 'Error deleting project.');
        return;
      }
      toast.success(res.message || 'Project deleted');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(error?.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const handleRestore = async (projectId) => {
    try {
      const res = await restoreProject(projectId);
      if (!res.success) {
        toast.error(res.data?.errorMessage || 'Error restoring project.');
        return;
      }
      toast.success(res.message || 'Project restored');
      fetchProjects();
    } catch (error) {
      console.error('Error restoring project:', error);
      toast.error(error?.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor(row => row.id, {
      id: 'id',
      header: 'ID',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor(row => row.name, {
      id: 'name',
      header: 'Name',
      cell: info => {
        const project = info.row.original;
        return (
          <>
            {info.getValue()}
            {project.deletedAt && (
              <Badge bg="secondary" className="ms-2">Deleted</Badge>
            )}
          </>
        );
      },
    }),
    !isAnalyst && columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: info => {
        const project = info.row.original;
        const isDeleted = !!project.deletedAt;

        return (
          <div className="d-flex gap-2">
            {/* Edit allowed even if deleted? Keep or hide as you prefer */}
            {!isDeleted && (
              <AnchorTag onClick={() => handleEdit(project)} hoverText="Edit">
                <Pencil />
              </AnchorTag>
            )}

            {isDeleted ? (
              <AnchorTag onClick={() => handleRestore(project.id)} hoverText="Restore">
                <Check2Circle />
              </AnchorTag>
            ) : (
              <AnchorTag onClick={() => handleDelete(project.id)} hoverText="Delete">
                <XOctagon />
              </AnchorTag>
            )}
          </div>
        );
      },
    }),
  ].filter(Boolean);

  return (
    <Container>
      <div className="d-flex align-items-center mb-3 gap-2">
        <h2>Projects</h2>
        {!isAnalyst && (
          <Button
            onClick={() => {
              setSelectedProject(null);
              setShowModal(true);
            }}
          >
            Add New
          </Button>
        )}
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <ListTable tableId="projects"
            data={projects}
            columns={columns}
            onRowClick={(row) => {
              navigate(`/projects/${row.id}/reports`)}
            }
          />
          {showModal && (
            <EditProjectModal
              project={selectedProject}   // null => create mode
              show={showModal}
              refetch={fetchProjects}
              onClose={() => setShowModal(false)}
            />
          )}
        </>
      )}
    </Container>
  );
}

export default Projects;
